import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { apiErrorMessage } from "@/lib/admin-formatters";
import { logger } from "@/lib/default-logger";

/**
 * The prompts the platform generates with.
 *
 * Set once at platform level and inherited by every account — the same shape as the price book
 * and the voice catalogue: `platform_prompt` is a public-schema table, so one row is what every
 * workspace's agents are drafted from. The product owner's ask, in his words: "I just set it once
 * as a super admin, and then all the accounts automatically pull that in."
 *
 * Super-admin only, and there is no client-facing read at all — a client never sees a prompt.
 *
 * Paths never start with `/api` — the axios baseURL already ends in it.
 */

/** The prompts the platform owns. A closed set: each one has a caller that knows what it sends. */
export type PromptKey = "AGENT_PROMPT_GENERATION" | "LEAD_CLEANING" | "POST_CALL_PROMPT_GENERATION";

/** One editable prompt, with the contract the generation code holds it to. */
export interface PlatformPrompt {
	id: string;
	key: PromptKey;
	/** What the setting is called. Authored on the API so the screen cannot describe a stale contract. */
	title: string;
	/** One line: what the prompt is handed. */
	receives: string;
	/** One line: the shape the answer has to take. */
	returns: string;
	/** Null on the platform default every account inherits. */
	accountId: string | null;
	accountName: string | null;
	promptText: string;
	/** The plugin's registered name. Null runs on whichever AI service the workspace has configured. */
	providerPluginKey: string | null;
	/** Null runs on the model that workspace's own AI service is set to. */
	model: string | null;
	version: number;
	/** Null while the row is still as it shipped — nobody typed it. */
	updatedByName: string | null;
	updatedAt: string;
}

/** A version that was saved. Append-only: nothing updates or deletes one. */
export interface PromptRevision {
	id: string;
	version: number;
	promptText: string;
	providerPluginKey: string | null;
	model: string | null;
	changedByName: string | null;
	createdAt: string;
}

/** A plugin a prompt can be pointed at, as discovered from the installed plugins. */
export interface PromptRunner {
	key: string;
	title: string;
}

/** Absent keys are left alone; an explicit null clears one. */
export interface PromptPayload {
	version: number;
	promptText?: string;
	providerPluginKey?: string | null;
	model?: string | null;
}

export const promptSettingsKeys = {
	all: ["prompt-settings"] as const,
	list: () => [...promptSettingsKeys.all, "list"] as const,
	runners: () => [...promptSettingsKeys.all, "runners"] as const,
	revisions: (id: string) => [...promptSettingsKeys.all, "revisions", id] as const,
};

/** The platform prompts, and any account overrides of them. A 403 here is an answer, not a bug. */
export function usePlatformPrompts(enabled = true) {
	return useQuery({
		queryKey: promptSettingsKeys.list(),
		queryFn: async (): Promise<PlatformPrompt[]> => {
			const response = await apiClient.get("/admin/prompts");
			return response.data;
		},
		enabled,
		refetchOnWindowFocus: false,
	});
}

/**
 * The plugins that can run a prompt.
 *
 * Discovered from the installed plugins rather than listed in this file, so a plugin added to the
 * backend appears in the selector without anyone editing a screen.
 */
export function usePromptRunners(enabled = true) {
	return useQuery({
		queryKey: promptSettingsKeys.runners(),
		queryFn: async (): Promise<PromptRunner[]> => {
			const response = await apiClient.get("/admin/prompts/runners");
			return response.data;
		},
		enabled,
		// Plugins are discovered at boot. This list changes when the API is redeployed.
		staleTime: 30 * 60 * 1000,
	});
}

/** Every version this prompt has held, newest first, each with the text that was saved. */
export function usePromptRevisions(id: string | null, enabled = true) {
	return useQuery({
		queryKey: promptSettingsKeys.revisions(id ?? ""),
		queryFn: async (): Promise<PromptRevision[]> => {
			const response = await apiClient.get(`/admin/prompts/${id}/revisions`);
			return response.data;
		},
		enabled: enabled && Boolean(id),
	});
}

/**
 * Save a new version.
 *
 * The API mints the version and appends the revision; a save that changes nothing is answered
 * with the row untouched rather than a new version, so history stays worth reading.
 */
export function useSavePrompt() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: PromptPayload }): Promise<PlatformPrompt> => {
			const response = await apiClient.patch(`/admin/prompts/${id}`, data);
			return response.data;
		},
		onSuccess: (prompt) => {
			queryClient.setQueryData<PlatformPrompt[]>(promptSettingsKeys.list(), (current) =>
				current?.map((row) => (row.id === prompt.id ? prompt : row))
			);
			// Not `all`: the runner list is a 30-minute cache of installed plugins and a prompt edit
			// tells us nothing new about it.
			queryClient.invalidateQueries({ queryKey: promptSettingsKeys.list() });
			queryClient.invalidateQueries({ queryKey: promptSettingsKeys.revisions(prompt.id) });
			toast.success(`Saved as version ${prompt.version}`);
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(apiErrorMessage(error, "Could not save the prompt"));
			if (error?.response?.status === 409) {
				queryClient.invalidateQueries({ queryKey: promptSettingsKeys.list() });
			}
		},
	});
}

/**
 * Put an account back on the platform default.
 *
 * Only an override can go — the API refuses to delete a platform row, because deleting it would
 * leave every account with nothing to resolve.
 */
export function useRemovePromptOverride() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string): Promise<void> => {
			await apiClient.delete(`/admin/prompts/${id}`);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: promptSettingsKeys.list() });
			toast.success("Back on the platform default");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(apiErrorMessage(error, "Could not remove the override"));
		},
	});
}
